import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Space, Tooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { fetchLsegEodQuota, refreshLsegEodQuota, selectors } from 'state';

const utcMonth = () => new Date().toISOString().slice(0, 7);

const LsegEodQuota: React.FC = () => {
  const dispatch = useDispatch();
  const { snapshot, loading, refreshing, error } = useSelector(
    selectors.lsegEodQuota,
  );
  const [currentMonth, setCurrentMonth] = useState(utcMonth);

  useEffect(() => {
    // This only reads PM's persisted snapshot; it never queries LSEG.
    dispatch(fetchLsegEodQuota({}));
    // Mark a prior-period snapshot even if this tab stays open over month-end.
    const clock = window.setInterval(() => setCurrentMonth(utcMonth()), 60000);
    return () => window.clearInterval(clock);
  }, [dispatch]);

  const hasQuota = snapshot?.count != null && snapshot?.limit != null;
  const oldPeriod = Boolean(snapshot?.period && snapshot.period < currentMonth);
  const problem = error || snapshot?.last_error;
  const lastChecked = snapshot?.checked_at
    ? new Date(snapshot.checked_at).toLocaleString()
    : 'Never';
  const details = (
    <div>
      <div>Provider period: {snapshot?.period || 'Not yet available'}</div>
      <div>Last checked: {lastChecked}</div>
      {hasQuota && (
        <div>{Math.max(0, snapshot!.limit! - snapshot!.count!)} remaining</div>
      )}
      {oldPeriod && <div>Awaiting quota for the new period.</div>}
      {problem && <div>{problem}</div>}
    </div>
  );

  return (
    <Space size={4} style={{ whiteSpace: 'nowrap' }}>
      <Tooltip title={details}>
        <span aria-live="polite">
          LSEG Eod quota{snapshot?.period ? ` (${snapshot.period})` : ''}:{' '}
          <strong>
            {hasQuota
              ? `${snapshot!.count} / ${snapshot!.limit}`
              : loading
                ? 'Loading…'
                : problem
                  ? 'Unavailable'
                  : 'Awaiting first EoD fetch'}
          </strong>
          {hasQuota &&
            ` · ${Math.max(0, snapshot!.limit! - snapshot!.count!)} remaining`}
          {hasQuota && (oldPeriod || problem) && ' · Outdated'}
        </span>
      </Tooltip>
      <Tooltip title="Refresh quota from LSEG">
        <Button
          type="text"
          size="small"
          icon={<ReloadOutlined />}
          loading={refreshing}
          disabled={refreshing}
          aria-label="Refresh quota from LSEG"
          onClick={() => dispatch(refreshLsegEodQuota({}))}
        />
      </Tooltip>
    </Space>
  );
};

export default LsegEodQuota;
